<template>
  <div class="MortgageForm">
    <h2 class="gl_form-title small">
      {{ t('copy_life_mortgage-form_title') }}
    </h2>

    <CardInfoComponent
      v-if="message"
      :icon="message.icon"
    >
      {{ message.label }}
    </CardInfoComponent>

    <BaseAutocompleteInputStrict
      id="entity"
      :label="t('copy_summary-step_mortgage-data_entity-label')"
      v-model="entity"
      :items="entities"
      :placeholder="t('copy_summary-step_mortgage-data_entity-placeholder')"
      :rules="[required]"
    />

    <BaseInput
      id="loan-number"
      :label="t('copy_summary-step_mortgage-data_loan-number-label')"
      v-model="loanNumber"
      :maxlength="50"
      :placeholder="t('copy_summary-step_mortgage-data_loan-number-placeholder')"
      :rules="[required]"
    />

    <MonthYearComponent
      id="expiration-date"
      :legend="t('copy_summary-step_mortgage-data_expiration-label')"
      v-model:month="expirationDate.month"
      v-model:year="expirationDate.year"
      :validDateRule="futureDate"
    />
  </div>
</template>

<script lang="ts" setup>
import { ref, onBeforeMount, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useLoadingStore } from '@/modules/core/stores/loadingStore'
import { useLifeStore } from '@/modules/life/hiring/stores'
import { useRules } from '@/modules/core/composables/useRulesComposable'
import type LifeConfigurations from '@/modules/life/common/models/APP/LifeConfigurations'
import type LifeHiringAnswers from '../../models/APP/LifeHiringAnswers'
import BaseAutocompleteInputStrict from '@/modules/core/components/ui/BaseAutocompleteInputStrict.vue'
import BaseInput from '@/modules/core/components/ui/BaseInput.vue'
import MonthYearComponent from '@/modules/core/components/formFields/MonthYearComponent.vue'
import { addNecessaryLeadingZeros } from '@/utils/textUtils'
import LifeMortgageClause from '../../models/APP/LifeMortgageClause'
import CardInfoComponent from '@/modules/common/common/components/panels/CardInfoComponent.vue'

const { t } = useI18n()
const loadingStore = useLoadingStore()
const lifeStore = useLifeStore()
const { required, futureDate } = useRules()

defineExpose({ triggerSubmit })

interface PropsInterface {
  nextForm: String[]
  answers: LifeHiringAnswers
  configurations: LifeConfigurations
}
const props = withDefaults(defineProps<PropsInterface>(), {})

const insured = props.answers.insureds[0]
const entities = ref<string[]>([])
const entity = ref(getInitEntity())
const loanNumber = ref(getInitLoanNumber())
const expirationDate = ref(getInitExpirationDate())

const message = computed(() => {
  return props.configurations.tags.mortgageLoanAlert
})

function getInitEntity(): string {
  const mortgageSaved = insured.mortgageClauses[0]
  return mortgageSaved?.entity ?? null
}
function getInitLoanNumber(): string {
  const mortgageSaved = insured.mortgageClauses[0]
  return mortgageSaved?.loanNumber ?? ''
}
function getInitExpirationDate(): { month: string; year: string } {
  const mortgageSaved = insured.mortgageClauses[0]
  const date = mortgageSaved?.expirationDate
  if (!date) {
    return { month: '', year: '' }
  }

  const d = new Date(date)
  return {
    month: addNecessaryLeadingZeros(d.getMonth() + 1),
    year: String(d.getFullYear())
  }
}

function triggerSubmit() {
  const page = props.nextForm[0]

  const { year, month } = expirationDate.value
  const mortgageClause = new LifeMortgageClause({
    entity: entity.value,
    loanNumber: loanNumber.value,
    expirationDate: `${year}-${month}-01T00:00:00`
  })

  const insureds = props.answers.insureds
  insureds[0].mortgageClauses = [mortgageClause]

  const answers = { insureds }

  return { page, answers }
}

async function beforeMount() {
  try {
    loadingStore.startLoading()
    entities.value = await lifeStore.fetchEntities()
  } catch (error) {
    console.log(error)
  } finally {
    loadingStore.stopLoading()
  }
}

onBeforeMount(beforeMount)
</script>
